import get from 'lodash.get';
import { of, EMPTY, concat, forkJoin, merge, from } from 'rxjs';
import { catchError, mergeMap, take, takeUntil, filter } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { NotificationService, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import Global from '../../../store/global-selectors';
import { DocumentService, isPrintSettingSetToDownload } from '../../../services/document-service';
import { BulkErrorsAndWarningsService } from '../../../services/bulk-errors-and-warnings-service';
import * as DocumentActions from '../../../store/documents/documents.actions';
import { DocumentType } from '../../../enums/document-type';
import * as Actions from './batches.print.actions';
import { bulkErrorsAndWarningsModalObservable } from '../../bulk-errors-and-warnings/bulk-errors-and-warnings.observables';
import * as PrintActions from '../../../store/printing/print.actions';
import * as PrintApi from '../../printing/print.api';
import { batchPackingSlip } from '../../printing/document-type-epic-helpers/batch-packing-slip';
import * as BatchApi from '../../../store/batches/batches.api';
import { determineShouldShowPrintDialog, getQuickshipPropsFromState } from '../../quickship/quickship.helpers';
import { getPrintSettingAndCheckIsOfflinePrinter } from '../../shipstation-connect/ss-connect.helpers';
import { LogService } from '../../../services/log-service';
import { createBatchPackingSlipsAsync } from '../../printing/async-printing/batch-packing-slips';
import {
  printDocumentRequest,
  PRINT_DOCUMENT_SUCCESS,
  PRINT_DOCUMENT_FAILURE,
  PRINT_DOCUMENT_REQUEST,
} from '../../shipstation-connect/ss-connect.actions';
import { getBatchDetails } from '../details/batch.details.actions';
import { getIsBatchDetailsPage } from '../batches.helpers';
import { downloadDocument } from '../../documents/document.helpers';

const getFlattnedAndUniquePlanPrintedData = (data) => {
  const fpIdSet = new Set();
  const flattenedUniqueData = data
    .reduce((acc, datumForSet) => [...acc, ...datumForSet])
    .reduce((acc, fpData) => {
      if (!fpIdSet.has(fpData.fulfillmentPlanId)) {
        fpIdSet.add(fpData.fulfillmentPlanId);
        acc.push(fpData);
      }
      return acc;
    }, []);
  return flattenedUniqueData;
};

export const checkForLabelPrintedEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.BATCH_PRINT_LABEL_PRINTED_CHECK_START),
    mergeMap((action) => {
      const { fulfillmentPlanSetIds, autoPrint, shouldIgnoreQuickshipSettings } = action;

      if (!fulfillmentPlanSetIds) {
        return EMPTY;
      }
      const checkStatusForFpSet = fulfillmentPlanSetIds.map((id) => BatchApi.checkLabelPrintedStatus(id));
      return forkJoin(...checkStatusForFpSet).pipe(
        mergeMap((data) => {
          const flattenedUniqueData = getFlattnedAndUniquePlanPrintedData(data);
          const state = getState();
          const quickshipProps = getQuickshipPropsFromState(state);
          const currentUserId = Global.selectUserCurrentUserId(state);
          const alreadyPrintedLabels = flattenedUniqueData
            .filter((x) => x.isPrinted)
            .map((warn) => ({
              id: warn.fulfillmentPlanId,
              fulfillmentPlanId: warn.fulfillmentPlanId,
            }));
          const labelCount = get(flattenedUniqueData, 'length', 0);
          const alreadyPrintedCount = alreadyPrintedLabels.length;

          if (!alreadyPrintedCount || (!shouldIgnoreQuickshipSettings && !determineShouldShowPrintDialog(quickshipProps))) {
            return of(
              Actions.printLabelRequest(
                fulfillmentPlanSetIds,
                autoPrint,
                labelCount,
                alreadyPrintedCount,
                [],
                shouldIgnoreQuickshipSettings,
              ),
            );
          }

          const errorsAndWarnings = BulkErrorsAndWarningsService.getBatchesLabelPrintedWarning(alreadyPrintedLabels, currentUserId);

          const onContinueAction = (updatedData) => {
            return Actions.printLabelRequest(
              fulfillmentPlanSetIds,
              autoPrint,
              labelCount,
              alreadyPrintedCount,
              updatedData.errorsAndWarnings,
              shouldIgnoreQuickshipSettings,
            );
          };
          const errorModal$ = bulkErrorsAndWarningsModalObservable({
            continueCanBeDisabled: false,
            dataType: 'batchedFulfillmentPlan',
            errorsAndWarnings: [errorsAndWarnings],
            fulfillmentPlanSetId: fulfillmentPlanSetIds,
            hasAdditionalAction: true,
            hasSelectedItems: !!flattenedUniqueData.length,
            isBatchPrinting: true,
            onContinueAction,
            selectedItems: flattenedUniqueData,
            title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
          });

          return errorModal$;
        }),
        catchError((error) => {
          LogService.logError(error);
          return EMPTY;
        }),
      );
    }),
  );
};

export const printLabelEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.BATCH_GRID_PRINT_LABEL_REQUEST),
    mergeMap((action) => {
      const state = getState();
      const quickshipProps = getQuickshipPropsFromState(state);

      const { fulfillmentPlanSetIds, labelCount, alreadyPrintedCount, errorsAndWarnings, shouldIgnoreQuickshipSettings } = action;
      if (!fulfillmentPlanSetIds) {
        return EMPTY;
      }

      const excludeAlreadyPrinted = get(errorsAndWarnings, 'length', 0) === 0;
      const url = `${DocumentType.BatchLabel.apiDownloadPath}?fulfillmentPlanSetIds=${fulfillmentPlanSetIds}&excludeAlreadyPrinted=${excludeAlreadyPrinted}`;

      const { printSetting, isOfflinePrinter } = getPrintSettingAndCheckIsOfflinePrinter({
        state,
        printSettingKey: DocumentType.BatchLabel.printSettingKey,
      });

      if (isOfflinePrinter) {
        return EMPTY;
      }

      if (isPrintSettingSetToDownload(printSetting)) {
        return concat(
          of(DocumentActions.downloadDocument(DocumentType.BatchLabel, fulfillmentPlanSetIds, null, url)),
          of(PrintActions.manuallySetPrintingDocCount(labelCount)),
        );
      }

      if (printSetting && !printSetting.disabled) {
        const print$ = DocumentActions.printDocument(
          DocumentType.BatchLabel,
          fulfillmentPlanSetIds,
          DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.BatchLabel, false),
          undefined,
          url,
        );
        return of(print$);
      }

      if (!shouldIgnoreQuickshipSettings && !determineShouldShowPrintDialog(quickshipProps)) {
        // none of the valid print conditions above are true, so abandon printing effort for QS
        return EMPTY;
      }

      const labelsToPrintCount = excludeAlreadyPrinted ? labelCount - alreadyPrintedCount : labelCount;

      return concat(
        of(PrintActions.openPrintModal(url, DocumentType.BatchLabel.identifier, fulfillmentPlanSetIds)),
        of(PrintActions.manuallySetPrintingDocCount(labelsToPrintCount)),
      );
    }),
  );
};

export const checkForPackingSlipPrintedEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.BATCH_PACKING_SLIP_PRINTED_CHECK_START),
    mergeMap((action) => {
      const { fulfillmentPlanSetIds, autoPrint } = action;
      if (!fulfillmentPlanSetIds) {
        return EMPTY;
      }

      const checkStatusForFpSet = fulfillmentPlanSetIds.map((id) => BatchApi.checkPackingSlipPrintedStatus(id));
      return forkJoin(...checkStatusForFpSet).pipe(
        mergeMap((data) => {
          const state = getState();
          const currentUserId = Global.selectUserCurrentUserId(state);
          const flattenedUniqueData = getFlattnedAndUniquePlanPrintedData(data);

          const alreadyPrintedPackingSlips = flattenedUniqueData
            .filter((x) => x.isPrinted)
            .map((warn) => ({
              id: warn.fulfillmentPlanId,
              fulfillmentPlanId: warn.fulfillmentPlanId,
            }));

          const documentCount = get(flattenedUniqueData, 'length', 0);
          const alreadyPrintedCount = alreadyPrintedPackingSlips.length;
          const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

          const fulfillmentPlanIds = flattenedUniqueData.map(({ fulfillmentPlanId }) => fulfillmentPlanId);
          if (!alreadyPrintedCount || !shouldShowReprintWarning) {
            return of(
              Actions.printPackingSlipRequest({
                fulfillmentPlanSetIds,
                autoPrint,
                documentCount,
                alreadyPrintedCount,
                fulfillmentPlanIds,
              }),
            );
          }

          const errorsAndWarnings = BulkErrorsAndWarningsService.getBatchesPackingSlipPrintedWarning(
            alreadyPrintedPackingSlips,
            currentUserId,
          );
          const onContinueAction = (updatedData) => {
            return Actions.printPackingSlipRequest({
              fulfillmentPlanSetIds,
              autoPrint,
              documentCount,
              alreadyPrintedCount,
              errorsAndWarnings: updatedData.errorsAndWarnings,
              fulfillmentPlanIds,
            });
          };
          const errorModal$ = bulkErrorsAndWarningsModalObservable({
            continueCanBeDisabled: false,
            dataType: 'batchedFulfillmentPlan',
            errorsAndWarnings: [errorsAndWarnings],
            fulfillmentPlanSetId: fulfillmentPlanSetIds,
            hasAdditionalAction: true,
            hasSelectedItems: !!flattenedUniqueData.length,
            isBatchPrinting: true,
            onContinueAction,
            selectedItems: flattenedUniqueData,
            title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
          });

          return errorModal$;
        }),
        catchError((error) => {
          LogService.logError(error);
          return EMPTY;
        }),
      );
    }),
  );
};

const printedSignal = (action$, printJob) => {
  return action$.pipe(
    takeUntil(
      action$.pipe(
        ofType(PRINT_DOCUMENT_FAILURE),
        filter((action) => action.requestId === printJob.requestId),
      ),
    ),
    ofType(PRINT_DOCUMENT_SUCCESS),
    filter((action) => {
      return action.request === printJob.requestId;
    }),
    take(1),
  );
};

const markPackingSlipPrinted = (signal$, printJob, docCount, history) => {
  return signal$.pipe(
    mergeMap((action) => {
      if (!printJob.ids.length || !printJob.requestId) {
        LogService.logError(new Error('Print job needs jobId and requestId'), {
          printJob,
          action,
        });
        return EMPTY;
      }
      const baseNotificationProps = {
        notificationType: 'info',
        duration: NOTIFICATION_DURATIONS.SHORT,
      };
      const printerName = printJob?.printer?.name || 'printer';
      const actionNotificationProps = batchPackingSlip.getPrintedSuccessNotificationProps(docCount, printerName);
      NotificationService.showNotification({
        ...baseNotificationProps,
        ...actionNotificationProps,
      });

      const isPrinted = true;
      return merge(
        from(PrintApi.markFullfillmentPlanSetsPackingSlipPrinted(printJob.ids, isPrinted)).pipe(
          // noop matches existing packing slip behavior
          catchError(() => EMPTY),
          mergeMap(() => {
            // reload the batch details grid to reflect the updated printed status
            if (getIsBatchDetailsPage(history)) {
              return of(getBatchDetails(printJob.ids[0]));
            }
            return EMPTY;
          }),
        ),
        batchPackingSlip.getPrintedSuccessObservable(printJob),
      );
    }),
  );
};

export const printPackingSlipEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.BATCH_GRID_PRINT_PACKING_SLIP_REQUEST),
    mergeMap((action) => {
      const state = getState();
      const { fulfillmentPlanSetIds, documentCount, alreadyPrintedCount, autoPrint, errorsAndWarnings, fulfillmentPlanIds } = action;

      if (!fulfillmentPlanSetIds) {
        return EMPTY;
      }

      const excludeAlreadyPrinted = get(errorsAndWarnings, 'length', 0) === 0;

      const { printSetting, isOfflinePrinter } = getPrintSettingAndCheckIsOfflinePrinter({
        state,
        printSettingKey: DocumentType.BatchPackingSlip.printSettingKey,
      });
      if (isOfflinePrinter) {
        return EMPTY;
      }

      const packingSlipsToPrintCount = excludeAlreadyPrinted ? documentCount - alreadyPrintedCount : documentCount;
      return createBatchPackingSlipsAsync({
        fulfillmentPlanSetIds,
        excludeAlreadyPrinted,
        printer: printSetting,
        documentCount: packingSlipsToPrintCount,
      }).pipe(
        mergeMap((printJob) => {
          if (isPrintSettingSetToDownload(printSetting)) {
            return merge(
              of(...downloadDocument(printJob.printUrl, DocumentType.BatchPackingSlip, printJob.ids)),
              of(PrintActions.manuallySetPrintingDocCount(packingSlipsToPrintCount)),
              markPackingSlipPrinted(of(printJob), printJob, packingSlipsToPrintCount, history),
            );
          }

          if (autoPrint || (printSetting && !printSetting.disabled)) {
            return concat(
              of(printDocumentRequest(printJob)),
              of(PrintActions.manuallySetPrintingDocCount(packingSlipsToPrintCount)),
              markPackingSlipPrinted(printedSignal(action$, printJob), printJob, packingSlipsToPrintCount, history),
            );
          }

          return concat(
            of(
              PrintActions.openPrintModal(
                printJob.printUrl,
                DocumentType.BatchPackingSlip.identifier,
                fulfillmentPlanIds,
                undefined,
                fulfillmentPlanSetIds,
              ),
            ),
            of(PrintActions.manuallySetPrintingDocCount(packingSlipsToPrintCount)),
            action$.pipe(
              takeUntil(action$.pipe(ofType(PrintActions.PRINT_MODAL_CLOSING))),
              ofType(PRINT_DOCUMENT_REQUEST),
              take(1),
              mergeMap((printRequestAction) => {
                // the print modal creates a new print job, so use that one instead of the original
                return markPackingSlipPrinted(
                  printedSignal(action$, printRequestAction.printJob),
                  printRequestAction.printJob,
                  packingSlipsToPrintCount,
                  history,
                );
              }),
            ),
          );
        }),
      );
    }),
  );
};

export const labelBatchPrintOtherForms = (action$) => {
  return action$.pipe(
    ofType(Actions.BATCH_PRINT_OTHER_FORMS),
    mergeMap(({ fulfillmentPlanSetId }) => {
      const url = `${DocumentType.BatchCustomsForm.apiDownloadPath}${fulfillmentPlanSetId}`;
      return concat(of(PrintActions.openPrintModal(url, DocumentType.OtherForms.identifier, fulfillmentPlanSetId)));
    }),
  );
};
